import {
    type CSSProperties,
    type ReactNode,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    Menubar,
    Button,
    Text
} from "ncore-ui-kit";
import {
    BulletList,
    InlineCode,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    FileIcon,
    UserIcon,
    StarIcon
} from "../../anatomy/icons";

const NAME = "Menubar";

const USAGE_CODE = `const [isSidebar, setIsSidebar] = useState(true);

<Menubar
    aria-label="Report editor"
    variant="outline"
>
    <Menubar.Menu
        menuKey="file"
    >
        <Menubar.Trigger
            title="File"
        />
        <Menubar.Content>
            <Menubar.Item
                onPress={createReport}
                title="New report"
                shortcut="mod+N"
            />
            <Menubar.Separator/>
            <Menubar.Item
                onPress={deleteReport}
                title="Delete report"
                type="danger"
            />
        </Menubar.Content>
    </Menubar.Menu>
    <Menubar.Menu
        menuKey="view"
    >
        <Menubar.Trigger
            title="View"
        />
        <Menubar.Content>
            <Menubar.CheckboxItem
                onChange={setIsSidebar}
                isChecked={isSidebar}
                title="Sidebar"
            />
        </Menubar.Content>
    </Menubar.Menu>
</Menubar>`;

const CONTROLLED_CODE = `const [openMenu, setOpenMenu] = useState<string | null>(null);

<Menubar
    onOpenChange={setOpenMenu}
    openMenu={openMenu}
>
    ...
</Menubar>`;

const CELL_STYLE: CSSProperties = {
    flexDirection: "column",
    display: "flex",
    gap: 8
};

const NARROW_STYLE: CSSProperties = {
    flexDirection: "column",
    display: "flex",
    width: 168
};

const ROW_STYLE: CSSProperties = {
    flexWrap: "wrap",
    display: "flex",
    gap: 8
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("section.variants"),
        id: "variants"
    },
    {
        title: t("menubarPage.contentTitle"),
        id: "content"
    },
    {
        title: t("menubarPage.collapseTitle"),
        id: "collapse"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const MenubarPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        lastAction,
        setLastAction
    ] = useState("");

    const [
        isSidebar,
        setIsSidebar
    ] = useState(true);

    const [
        isLineNumbers,
        setIsLineNumbers
    ] = useState(false);

    const [
        isBold,
        setIsBold
    ] = useState(true);

    const [
        isItalic,
        setIsItalic
    ] = useState(false);

    const [
        alignment,
        setAlignment
    ] = useState("left");

    const [
        openMenu,
        setOpenMenu
    ] = useState<string | null>(null);

    const handleCommand = (label: string) => () => setLastAction(label);

    const handleOpenFile = () => setOpenMenu("file");

    const handleOpenEdit = () => setOpenMenu("edit");

    const handleCloseMenu = () => setOpenMenu(null);

    const renderCell = (label: string, node: ReactNode) => <div
        style={CELL_STYLE}
        key={label}
    >
        <Text
            variant="labelSmallSize"
            color="mid"
        >
            {label}
        </Text>
        {node}
    </div>;

    const renderFileMenu = () => <Menubar.Menu
        menuKey="file"
        key="file"
    >
        <Menubar.Trigger
            title={t("menubarPage.menuFile")}
        />
        <Menubar.Content>
            <Menubar.Item
                onPress={handleCommand(t("menubarPage.newReport"))}
                title={t("menubarPage.newReport")}
                shortcut="mod+N"
            />
            <Menubar.Item
                onPress={handleCommand(t("menubarPage.open"))}
                title={t("menubarPage.open")}
                shortcut="mod+O"
            />
            <Menubar.Sub>
                <Menubar.SubTrigger
                    title={t("menubarPage.export")}
                />
                <Menubar.SubContent>
                    <Menubar.Item
                        onPress={handleCommand(t("menubarPage.exportPdf"))}
                        title={t("menubarPage.exportPdf")}
                    />
                    <Menubar.Item
                        onPress={handleCommand(t("menubarPage.exportMarkdown"))}
                        title={t("menubarPage.exportMarkdown")}
                    />
                </Menubar.SubContent>
            </Menubar.Sub>
            <Menubar.Separator/>
            <Menubar.Item
                onPress={handleCommand(t("menubarPage.save"))}
                title={t("menubarPage.save")}
                shortcut="mod+S"
            />
            <Menubar.Item
                onPress={handleCommand(t("menubarPage.deleteReport"))}
                title={t("menubarPage.deleteReport")}
                type="danger"
            />
        </Menubar.Content>
    </Menubar.Menu>;

    const renderEditMenu = () => <Menubar.Menu
        menuKey="edit"
        key="edit"
    >
        <Menubar.Trigger
            title={t("menubarPage.menuEdit")}
        />
        <Menubar.Content>
            <Menubar.Item
                onPress={handleCommand(t("menubarPage.undo"))}
                title={t("menubarPage.undo")}
                shortcut="mod+Z"
            />
            <Menubar.Item
                onPress={handleCommand(t("menubarPage.redo"))}
                title={t("menubarPage.redo")}
                shortcut="mod+shift+Z"
            />
            <Menubar.Separator/>
            <Menubar.Item
                onPress={handleCommand(t("menubarPage.copy"))}
                title={t("menubarPage.copy")}
                shortcut="mod+C"
            />
            <Menubar.Item
                title={t("menubarPage.paste")}
                isDisabled={true}
                shortcut="mod+V"
            />
        </Menubar.Content>
    </Menubar.Menu>;

    const renderViewMenu = () => <Menubar.Menu
        menuKey="view"
        key="view"
    >
        <Menubar.Trigger
            title={t("menubarPage.menuView")}
        />
        <Menubar.Content>
            <Menubar.CheckboxItem
                title={t("menubarPage.sidebar")}
                onChange={setIsSidebar}
                isChecked={isSidebar}
            />
            <Menubar.CheckboxItem
                title={t("menubarPage.lineNumbers")}
                onChange={setIsLineNumbers}
                isChecked={isLineNumbers}
            />
        </Menubar.Content>
    </Menubar.Menu>;

    const renderHelpMenu = (isDisabled: boolean = false) => <Menubar.Menu
        menuKey="help"
        key="help"
    >
        <Menubar.Trigger
            title={t("menubarPage.menuHelp")}
            isDisabled={isDisabled}
        />
        <Menubar.Content
            align="end"
        >
            <Menubar.Item
                onPress={handleCommand(t("menubarPage.docs"))}
                title={t("menubarPage.docs")}
            />
            <Menubar.Item
                onPress={handleCommand(t("menubarPage.about"))}
                title={t("menubarPage.about")}
            />
        </Menubar.Content>
    </Menubar.Menu>;

    const renderMenus = (isHelpDisabled: boolean = false) => [
        renderFileMenu(),
        renderEditMenu(),
        renderViewMenu(),
        renderHelpMenu(isHelpDisabled)
    ];

    return <Article>
        <ComponentHeader
            lead={t("menubarPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("menubarPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("menubarPage.overviewCaption", [
                    lastAction || t("menubarPage.noAction")
                ])}
                isStacked={true}
            >
                <Menubar
                    aria-label={t("menubarPage.barLabel")}
                    variant="outline"
                >
                    {renderMenus()}
                </Menubar>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("section.variants")}
            id="variants"
        >
            <Paragraph>
                {t("menubarPage.variantsText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    renderCell("variant=\"outline\"", <Menubar
                        aria-label={t("menubarPage.barLabel")}
                        variant="outline"
                    >
                        {renderFileMenu()}
                        {renderEditMenu()}
                    </Menubar>)
                }
                {
                    renderCell("variant=\"plain\"", <Menubar
                        aria-label={t("menubarPage.barLabel")}
                        variant="plain"
                    >
                        {renderFileMenu()}
                        {renderEditMenu()}
                    </Menubar>)
                }
            </Preview>
        </Section>
        <Section
            title={t("menubarPage.contentTitle")}
            id="content"
        >
            <Paragraph>
                {t("menubarPage.contentText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Menubar
                    aria-label={t("menubarPage.barLabel")}
                    variant="outline"
                >
                    <Menubar.Menu
                        menuKey="format"
                    >
                        <Menubar.Trigger
                            title={t("menubarPage.menuFormat")}
                        />
                        <Menubar.Content>
                            <Menubar.Label
                                title={t("menubarPage.textStyle")}
                            />
                            <Menubar.CheckboxItem
                                title={t("menubarPage.bold")}
                                onChange={setIsBold}
                                isChecked={isBold}
                                shortcut="mod+B"
                            />
                            <Menubar.CheckboxItem
                                title={t("menubarPage.italic")}
                                onChange={setIsItalic}
                                isChecked={isItalic}
                                shortcut="mod+I"
                            />
                            <Menubar.Separator/>
                            <Menubar.RadioGroup
                                title={t("menubarPage.alignment")}
                                onChange={setAlignment}
                                value={alignment}
                            >
                                <Menubar.RadioItem
                                    title={t("menubarPage.alignLeft")}
                                    value="left"
                                />
                                <Menubar.RadioItem
                                    title={t("menubarPage.alignCenter")}
                                    value="center"
                                />
                                <Menubar.RadioItem
                                    title={t("menubarPage.alignRight")}
                                    value="right"
                                />
                            </Menubar.RadioGroup>
                        </Menubar.Content>
                    </Menubar.Menu>
                    <Menubar.Menu
                        menuKey="insert"
                    >
                        <Menubar.Trigger
                            title={t("menubarPage.menuInsert")}
                        />
                        <Menubar.Content
                            align="end"
                        >
                            <Menubar.Item
                                subTitle={t("menubarPage.mentionHint")}
                                title={t("menubarPage.mention")}
                                icon={UserIcon}
                            />
                            <Menubar.Item
                                title={t("menubarPage.bookmark")}
                                icon={StarIcon}
                            />
                            <Menubar.Item
                                disabledReason={t("menubarPage.attachmentReason")}
                                title={t("menubarPage.attachment")}
                                isDisabled={true}
                                icon={FileIcon}
                            />
                        </Menubar.Content>
                    </Menubar.Menu>
                </Menubar>
            </Preview>
        </Section>
        <Section
            title={t("menubarPage.collapseTitle")}
            id="collapse"
        >
            <Paragraph>
                {t("menubarPage.collapseText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    renderCell("collapseBehaviour=\"never\"", <Menubar
                        aria-label={t("menubarPage.barLabel")}
                        collapseBehaviour="never"
                        variant="outline"
                    >
                        {renderMenus()}
                    </Menubar>)
                }
                {
                    renderCell("collapseBehaviour=\"always\"", <Menubar
                        aria-label={t("menubarPage.barLabel")}
                        collapseBehaviour="always"
                        variant="outline"
                    >
                        {renderMenus()}
                    </Menubar>)
                }
                {
                    renderCell(t("menubarPage.collapseNarrow"), <div
                        style={NARROW_STYLE}
                    >
                        <Menubar
                            aria-label={t("menubarPage.barLabel")}
                            collapseBehaviour="auto"
                            variant="outline"
                        >
                            {renderMenus()}
                        </Menubar>
                    </div>)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("menubarPage.statesText")}
            </Paragraph>
            <Preview
                caption={t("menubarPage.statesCaption", [
                    JSON.stringify(openMenu)
                ])}
                isStacked={true}
            >
                {
                    renderCell("openMenu", <Menubar
                        aria-label={t("menubarPage.barLabel")}
                        onOpenChange={setOpenMenu}
                        openMenu={openMenu}
                        variant="outline"
                    >
                        {renderMenus()}
                    </Menubar>)
                }
                <div
                    style={ROW_STYLE}
                >
                    <Button
                        title={t("menubarPage.menuFile")}
                        onPress={handleOpenFile}
                        spreadBehaviour="free"
                        variant="outline"
                        type="neutral"
                        size="small"
                    />
                    <Button
                        title={t("menubarPage.menuEdit")}
                        onPress={handleOpenEdit}
                        spreadBehaviour="free"
                        variant="outline"
                        type="neutral"
                        size="small"
                    />
                    <Button
                        title={t("menubarPage.close")}
                        onPress={handleCloseMenu}
                        spreadBehaviour="free"
                        variant="outline"
                        type="neutral"
                        size="small"
                    />
                </div>
                {
                    renderCell("Menubar.Trigger isDisabled={true}", <Menubar
                        aria-label={t("menubarPage.barLabel")}
                        variant="outline"
                    >
                        {renderMenus(true)}
                    </Menubar>)
                }
            </Preview>
            <CodeBlock
                code={CONTROLLED_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("menubarPage.a11yRoles"),
                    t("menubarPage.a11yTrigger"),
                    t("menubarPage.a11yTabStop"),
                    t("menubarPage.a11yBarKeys"),
                    t("menubarPage.a11yFocusBar"),
                    t("menubarPage.a11yClose"),
                    t("menubarPage.a11yHover"),
                    t("menubarPage.a11yItems"),
                    t("menubarPage.a11yCollapsed"),
                    t("menubarPage.a11yWeb"),
                    t("menubarPage.a11yWarnings")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("menubarPage.propVariant"),
                        type: "\"plain\" | \"outline\"",
                        defaultValue: "\"plain\"",
                        name: "variant"
                    },
                    {
                        description: t("menubarPage.propCollapseBehaviour"),
                        type: "\"auto\" | \"always\" | \"never\"",
                        defaultValue: "\"auto\"",
                        name: "collapseBehaviour"
                    },
                    {
                        description: t("menubarPage.propOpenMenu"),
                        type: "string | null",
                        name: "openMenu"
                    },
                    {
                        description: t("menubarPage.propOnOpenChange"),
                        type: "(menuKey: string | null) => void",
                        name: "onOpenChange"
                    },
                    {
                        description: t("menubarPage.propAriaLabel"),
                        name: "aria-label",
                        type: "string"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("menubarPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("menubarPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("menubarPage.propsNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    Menubar.Menu
                </InlineCode>
                {" "}
                {t("menubarPage.partMenuText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("menubarPage.propMenuKey"),
                        name: "menuKey",
                        type: "string"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    Menubar.Trigger
                </InlineCode>
                {" "}
                {t("menubarPage.partTriggerText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("menubarPage.propTriggerTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("menubarPage.propTriggerIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("menubarPage.propTriggerTestID"),
                        name: "testID",
                        type: "string"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("menubarPage.triggerNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    Menubar.Content
                </InlineCode>
                {" "}
                {t("menubarPage.partContentText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("menubarPage.propContentAlign"),
                        type: "\"start\" | \"end\"",
                        defaultValue: "\"start\"",
                        name: "align"
                    },
                    {
                        description: t("menubarPage.propContentWidthBehaviour"),
                        type: "\"content\" | \"trigger\"",
                        defaultValue: "\"content\"",
                        name: "widthBehaviour"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("menubarPage.contentNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default MenubarPage;
